import { useEffect, useMemo, useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import { Link } from "react-router-dom";
import { Loader2, X, Check, Sparkles, ShoppingBag, AlertCircle } from "lucide-react";

import { buyPack, getPack, listMyPurchases, listPacks } from "../../api/marketplace.api";
import useWalletStore from "../../store/wallet.store";

// Pack-type pill tones — match the visual language used elsewhere
// (players list, scorecard) for role pills.
const TYPE_TONE = {
  TOURNAMENT:    "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  SERIES:        "bg-blue-500/15 text-blue-300 border-blue-400/30",
  LEAGUE:        "bg-amber-500/15 text-amber-300 border-amber-400/30",
  TEAMS_ONLY:    "bg-slate-500/15 text-slate-200 border-slate-400/30",
  PLAYERS_ONLY:  "bg-purple-500/15 text-purple-200 border-purple-400/30",
  // MATCH_BUNDLE — view-grant scored-match packs (F6).
  MATCH_BUNDLE:  "bg-indigo-500/15 text-indigo-200 border-indigo-400/30",
  // 2026-06-12 — Officials + Venues bundles.
  SQUAD_PACK:    "bg-amber-500/15 text-amber-300 border-amber-400/30",
  OFFICIALS_PACK:"bg-sky-500/15 text-sky-200 border-sky-400/30",
  VENUE_PACK:    "bg-teal-500/15 text-teal-200 border-teal-400/30",
  // 2026-07-31 — TOURNAMENT_BUNDLE wraps N child packs into one listing.
  TOURNAMENT_BUNDLE: "bg-amber-500/15 text-amber-300 border-amber-400/30",
};

const fmtType = (t) =>
  ({
    TOURNAMENT:    "Tournament",
    SERIES:        "Series",
    LEAGUE:        "League",
    TEAMS_ONLY:    "Teams only",
    PLAYERS_ONLY:  "Players only",
    MATCH_BUNDLE:  "Scored matches",
    SQUAD_PACK:    "Squad",
    OFFICIALS_PACK:"Match officials",
    VENUE_PACK:    "Venues",
    TOURNAMENT_BUNDLE: "Tournament bundle",
  }[t] || t);

// E14 — packs published in the last 14 days surface in the "Just listed"
// carousel above the main grid. Keep the threshold liberal so testers
// see their freshly-published packs without waiting on a heavier "new"
// flag on the backend.
const NEW_WINDOW_DAYS = 14;
function isRecent(publishedAt) {
  if (!publishedAt) return false;
  const ts = new Date(publishedAt).getTime();
  if (!ts) return false;
  return (Date.now() - ts) < NEW_WINDOW_DAYS * 24 * 3600 * 1000;
}

export default function Marketplace() {
  const [packs, setPacks] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);

  const balance = useWalletStore((s) => s.balance);
  const fetchBalance = useWalletStore((s) => s.fetchBalance);

  const reload = () => {
    setErr("");
    Promise.all([listPacks(), listMyPurchases()])
      .then(([p, my]) => {
        setPacks(p.data || []);
        setPurchases(my.data || []);
      })
      .catch((e) =>
        setErr(
          e?.response?.data?.detail ||
            e?.message ||
            "Could not load the marketplace right now."
        )
      );
  };

  useEffect(() => {
    reload();
    fetchBalance();
  }, [fetchBalance]);

  const purchasedPackIds = useMemo(
    () => new Set(purchases.map((p) => p.pack)),
    [purchases]
  );

  // E14 — split into "Just listed" (≤14d) + everything else. The
  // recent set keeps the main grid still showing the same pack so
  // founder doesn't lose discoverability if they scroll past the
  // carousel.
  const recentPacks = useMemo(
    () => (packs || []).filter((p) => isRecent(p.published_at)).slice(0, 8),
    [packs],
  );

  return (
    <div className="min-h-screen bg-bg-main py-6 sm:py-10 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary mb-1 flex items-center gap-2">
              <ShoppingBag className="w-3.5 h-3.5" />
              Marketplace
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary">
              Ready-made tournament packs
            </h1>
            <p className="text-text-secondary text-sm mt-1 max-w-2xl">
              Skip 2 hours of player + team entry. Buy a pack with ZenCredits and
              the full squad lands in your library, ready to use in match setup.
              Every buyer gets their own copy — your scoring and stats stay
              private to your account.
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-text-secondary">Wallet:</span>
            <span className="px-3 py-1 rounded-full bg-bg-primary border border-border-default text-text-primary font-medium">
              {balance?.balance_credits ?? "—"} ZC
            </span>
            <Link
              to="/account/topup"
              className="text-button-primary hover:underline text-xs"
            >
              Top up
            </Link>
          </div>
        </div>

        {err && (
          <div className="mb-4 bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded-lg p-4 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {packs == null ? (
          <div className="text-text-secondary text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading packs…
          </div>
        ) : packs.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* E14 — "Just listed" horizontal-scroll carousel for fresh
                packs. Hidden when nothing is recent so dead real estate
                doesn't show. */}
            {recentPacks.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-text-primary font-semibold flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    Just listed
                  </h2>
                  <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                    last {NEW_WINDOW_DAYS} days
                  </span>
                </div>
                <div className="flex overflow-x-auto gap-3 -mx-3 sm:-mx-4 px-3 sm:px-4 snap-x snap-mandatory pb-2">
                  {recentPacks.map((p) => (
                    <div key={p.id} className="snap-start shrink-0 w-72">
                      <PackTile
                        pack={p}
                        owned={purchasedPackIds.has(p.id) || !!p.already_owned}
                        onClick={() => setOpenId(p.id)}
                        ribbon="NEW"
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}

            <h2 className="text-text-primary font-semibold mb-3">
              All packs ({packs.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {packs.map((p) => (
                <PackTile
                  key={p.id}
                  pack={p}
                  owned={purchasedPackIds.has(p.id) || !!p.already_owned}
                  onClick={() => setOpenId(p.id)}
                />
              ))}
            </div>
          </>
        )}

        {/* My purchases */}
        {purchases.length > 0 && (
          <div className="mt-12">
            <h2 className="text-text-primary font-semibold mb-3">
              My purchases ({purchases.length})
            </h2>
            <div className="bg-bg-primary border border-border-default rounded-xl divide-y divide-border-default">
              {purchases.map((row) => (
                <div
                  key={row.id}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-text-primary text-sm font-medium">
                      {row.pack_name}
                    </p>
                    <p className="text-text-secondary text-xs">
                      {[
                        fmtType(row.pack_type),
                        row.player_count > 0 && `${row.player_count} player${row.player_count === 1 ? "" : "s"}`,
                        row.team_count > 0 && `${row.team_count} team${row.team_count === 1 ? "" : "s"}`,
                        row.umpire_count > 0 && `${row.umpire_count} official${row.umpire_count === 1 ? "" : "s"}`,
                        row.venue_count > 0 && `${row.venue_count} venue${row.venue_count === 1 ? "" : "s"}`,
                        new Date(row.purchased_at).toLocaleDateString(),
                      ].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="text-text-secondary text-xs">
                    {row.price_credits_paid} ZC
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {openId && (
        <PackDetailModal
          packId={openId}
          onClose={() => setOpenId(null)}
          onPurchased={() => {
            reload();
            fetchBalance();
            setOpenId(null);
          }}
          onRefresh={() => {
            // Refresh library + wallet without closing the modal — used
            // when the buyer purchases a child pack from inside a
            // TOURNAMENT_BUNDLE and might pick more children after.
            reload();
            fetchBalance();
          }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-bg-primary border border-border-default rounded-xl p-10 text-center">
      <Sparkles className="w-8 h-8 text-text-secondary mx-auto mb-3" />
      <h3 className="text-text-primary font-semibold mb-1">
        No packs available yet
      </h3>
      <p className="text-text-secondary text-sm max-w-md mx-auto mb-4">
        We&apos;re curating tournament squads ahead of upcoming seasons. Check
        back closer to the start of your next tournament.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href="/cricket/players/add"
          className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded bg-button-primary text-white hover:brightness-110"
        >
          + Add your own players
        </a>
        <a
          href="/contact-sales"
          className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded bg-bg-secondary text-text-primary border border-border-default hover:border-button-primary/60"
        >
          Request a tournament pack →
        </a>
      </div>
    </div>
  );
}

function PackTile({ pack, owned, onClick, ribbon }) {
  // F6 — MATCH_BUNDLE packs are view-grant, not clone-on-buy. The
  // tile copy reflects "matches included" instead of "teams · players".
  // 2026-06-12 — OFFICIALS_PACK / VENUE_PACK get their own count copy.
  // 2026-07-31 — TOURNAMENT_BUNDLE shows "N packs inside" and doubles
  // as the primary tile type (squad/officials/venues packs are hidden
  // from the top-level list — they only appear inside a bundle).
  const isMatchBundle = pack.pack_type === "MATCH_BUNDLE";
  const isOfficials = pack.pack_type === "OFFICIALS_PACK";
  const isVenues = pack.pack_type === "VENUE_PACK";
  const isBundle = pack.pack_type === "TOURNAMENT_BUNDLE";
  const matchCount = pack.match_count ?? 0;
  const umpireCount = pack.umpire_count ?? 0;
  const venueCount = pack.venue_count ?? 0;
  const childCount = pack.child_pack_count ?? 0;
  const placeholder = isBundle
    ? "Tap to see the packs inside this tournament bundle."
    : isMatchBundle
      ? "Tap to see the matches included."
      : isOfficials
        ? "Tap to preview the included officials."
        : isVenues
          ? "Tap to preview the included venues."
          : "Tap to preview the included squad.";
  return (
    <button
      onClick={onClick}
      className="relative text-left bg-bg-primary border border-border-default hover:border-button-primary/60 rounded-xl p-5 transition group flex flex-col w-full"
    >
      {ribbon && (
        <span className="absolute top-3 right-3 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 border border-emerald-400/60">
          {ribbon}
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <span
          className={`inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${
            TYPE_TONE[pack.pack_type] ||
            "bg-bg-secondary text-text-secondary border-border-default"
          }`}
        >
          {fmtType(pack.pack_type)}
          {pack.season ? ` · ${pack.season}` : ""}
        </span>
        {owned && (
          <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Owned
          </span>
        )}
      </div>
      <h3 className="text-base font-semibold text-text-primary mb-1">
        {pack.name}
      </h3>
      <p className="text-text-secondary text-xs mb-4 line-clamp-2 grow">
        {pack.short_description || placeholder}
      </p>
      <div className="flex items-end justify-between mt-auto">
        <div className="text-xs text-text-secondary">
          {isBundle ? (
            <>
              <span className="text-text-primary font-medium">
                {childCount}
              </span>{" "}
              pack{childCount === 1 ? "" : "s"} inside · pick any or buy all
            </>
          ) : isMatchBundle ? (
            <>
              <span className="text-text-primary font-medium">
                {matchCount}
              </span>{" "}
              scored matches · view-only
            </>
          ) : isOfficials ? (
            <>
              <span className="text-text-primary font-medium">
                {umpireCount}
              </span>{" "}
              match official{umpireCount === 1 ? "" : "s"}
            </>
          ) : isVenues ? (
            <>
              <span className="text-text-primary font-medium">
                {venueCount}
              </span>{" "}
              venue{venueCount === 1 ? "" : "s"}
            </>
          ) : (
            <>
              <span className="text-text-primary font-medium">
                {pack.team_count}
              </span>{" "}
              teams ·{" "}
              <span className="text-text-primary font-medium">
                {pack.player_count}
              </span>{" "}
              players
            </>
          )}
        </div>
        <div className="text-right">
          <div className="text-button-primary font-semibold text-lg leading-tight">
            {pack.price_credits} ZC
          </div>
          <div className="text-[10px] text-text-secondary leading-tight">
            ₹{pack.price_credits.toLocaleString()}
            {!isMatchBundle && pack.player_count > 0 && (
              <span className="ml-1">· ₹{Math.round(pack.price_credits / pack.player_count)}/player</span>
            )}
            {isMatchBundle && matchCount > 0 && (
              <span className="ml-1">· ₹{Math.round(pack.price_credits / matchCount)}/match</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function BundleChildRow({ child, onBuy, busy, disabled }) {
  const owned = !!child.already_owned;
  const meta = TYPE_TONE[child.pack_type] ||
    "bg-bg-secondary text-text-secondary border-border-default";
  const summary = (() => {
    if (child.pack_type === "SQUAD_PACK") {
      return `${child.team_count || 0} teams · ${child.player_count || 0} players`;
    }
    if (child.pack_type === "OFFICIALS_PACK") {
      const n = child.umpire_count || 0;
      return `${n} match official${n === 1 ? "" : "s"}`;
    }
    if (child.pack_type === "VENUE_PACK") {
      const n = child.venue_count || 0;
      return `${n} venue${n === 1 ? "" : "s"}`;
    }
    return null;
  })();
  return (
    <div className="px-3 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${meta}`}>
            {fmtType(child.pack_type)}
          </span>
          <span className="text-sm text-text-primary font-medium truncate">
            {child.name}
          </span>
        </div>
        {summary && (
          <div className="text-[11px] text-text-secondary">{summary}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold text-text-primary">
          {child.price_credits} ZC
        </div>
        {owned ? (
          <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 inline-flex items-center gap-1 mt-1">
            <Check className="w-3 h-3" /> Owned
          </span>
        ) : (
          <button
            type="button"
            onClick={onBuy}
            disabled={busy || disabled}
            className="mt-1 px-2.5 py-1 text-[11px] font-semibold rounded border border-button-primary/60 text-button-primary hover:bg-button-primary/10 disabled:opacity-50 inline-flex items-center gap-1"
          >
            {busy ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Buying…</>
            ) : (
              "Buy just this"
            )}
          </button>
        )}
      </div>
    </div>
  );
}


function PackDetailModal({ packId, onClose, onPurchased, onRefresh }) {
  const [pack, setPack] = useState(null);
  const [buying, setBuying] = useState(false);
  // 2026-07-31 — child-pack purchases from inside a TOURNAMENT_BUNDLE
  // set this to the child's id so the row's button shows a spinner
  // (instead of the top-level "Buy full bundle" one).
  const [buyingChildId, setBuyingChildId] = useState(null);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const panelRef = useRef(null);
  const anyBuying = buying || !!buyingChildId;
  const safeClose = anyBuying ? () => {} : (onClose || (() => {}));
  useDialogDismiss({ open: true, onClose: safeClose, ref: panelRef });

  const reloadPack = () => {
    getPack(packId)
      .then((r) => setPack(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    getPack(packId)
      .then((r) => setPack(r.data))
      .catch((e) =>
        setErr(
          e?.response?.data?.detail || "Could not load this pack."
        )
      );
  }, [packId]);

  const handleBuy = async () => {
    setBuying(true);
    setErr("");
    setSuccess("");
    try {
      const r = await buyPack(packId);
      setSuccess(r.data?.message || "Pack purchased.");
      setTimeout(onPurchased, 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Purchase failed. Please try again.");
      setBuying(false);
    }
  };

  const handleBuyChild = async (childId) => {
    setBuyingChildId(childId);
    setErr("");
    setSuccess("");
    try {
      const r = await buyPack(childId);
      setSuccess(r.data?.message || "Pack purchased.");
      // Refresh the bundle detail so the child's "Owned" pill flips
      // without leaving the modal — the buyer might want to grab more
      // children from the same bundle. onRefresh reloads the parent's
      // pack list + wallet balance WITHOUT closing the modal.
      reloadPack();
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Purchase failed. Please try again.");
    } finally {
      setBuyingChildId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-3"
      role="presentation"
      onClick={() => !buying && onClose?.()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={pack?.name || "Marketplace pack"}
        className="bg-bg-main border border-border-default rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border-default flex items-start justify-between gap-3 sticky top-0 bg-bg-main">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">
              {pack?.name || "Loading…"}
            </h2>
            {pack && (
              <p className="text-text-secondary text-xs mt-0.5">
                {(() => {
                  const head = [
                    fmtType(pack.pack_type),
                    pack.season || null,
                  ].filter(Boolean).join(" · ");
                  let tail;
                  if (pack.pack_type === "MATCH_BUNDLE") {
                    tail = `${pack.match_count ?? pack.matches?.length ?? 0} scored matches · view-only`;
                  } else if (pack.pack_type === "OFFICIALS_PACK") {
                    const n = pack.umpire_count ?? pack.umpires?.length ?? 0;
                    tail = `${n} match official${n === 1 ? "" : "s"}`;
                  } else if (pack.pack_type === "VENUE_PACK") {
                    const n = pack.venue_count ?? pack.venues?.length ?? 0;
                    tail = `${n} venue${n === 1 ? "" : "s"}`;
                  } else {
                    tail = `${pack.player_count} players · ${pack.team_count} teams`;
                  }
                  return `${head} · ${tail}`;
                })()}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {!pack ? (
            <div className="text-text-secondary text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading pack…
            </div>
          ) : (
            <>
              {pack.description && (
                <p className="text-text-secondary text-sm mb-4 whitespace-pre-line">
                  {pack.description}
                </p>
              )}

              {pack.pack_type === "TOURNAMENT_BUNDLE" && pack.child_packs?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Packs in this bundle ({pack.child_packs.length})
                  </h3>
                  <div className="rounded-lg border border-border-default bg-bg-primary divide-y divide-border-default">
                    {pack.child_packs.map((c) => (
                      <BundleChildRow
                        key={c.id}
                        child={c}
                        onBuy={() => handleBuyChild(c.id)}
                        busy={buyingChildId === c.id}
                        disabled={anyBuying && buyingChildId !== c.id}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] text-text-secondary mt-2">
                    Pick individual packs above, or buy the whole bundle at
                    the bottom for <span className="text-text-primary font-medium">{pack.price_credits} ZC</span>
                    {(() => {
                      const alaCarte = pack.child_packs.reduce(
                        (s, c) => s + (c.price_credits || 0), 0,
                      );
                      if (alaCarte > pack.price_credits && pack.price_credits > 0) {
                        return (
                          <span className="text-emerald-300">
                            {" "}(saves {alaCarte - pack.price_credits} ZC vs à la carte)
                          </span>
                        );
                      }
                      return null;
                    })()}.
                  </p>
                </div>
              )}

              {pack.pack_type === "MATCH_BUNDLE" && pack.matches?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Matches in this bundle ({pack.matches.length})
                  </h3>
                  <div className="bg-bg-primary border border-border-default rounded-lg max-h-64 overflow-y-auto divide-y divide-border-default">
                    {pack.matches.map((m) => (
                      <div key={m.id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <div className="min-w-0">
                          <div className="text-text-primary truncate">{m.label}</div>
                          <div className="text-text-secondary">
                            {m.match_date || "?"}
                            {m.format && <span> · {m.format}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-text-secondary mt-2">
                    Buying grants view-only access via your analyzer dashboard.
                    Stats from these matches do not roll into your career
                    aggregates.
                  </p>
                </div>
              )}

              {pack.teams?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Teams ({pack.teams.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {pack.teams.map((t) => (
                      <span
                        key={t.id}
                        className="px-2 py-1 text-xs rounded bg-bg-primary border border-border-default text-text-primary"
                      >
                        {t.short_name || t.name}
                        {t.squad_size ? (
                          <span className="text-text-secondary ml-1">
                            ({t.squad_size})
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {pack.umpires?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Match officials ({pack.umpires.length})
                  </h3>
                  <div className="bg-bg-primary border border-border-default rounded-lg max-h-64 overflow-y-auto divide-y divide-border-default">
                    {pack.umpires.map((u) => (
                      <div key={u.id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <div>
                          <span className="text-text-primary">{u.name}</span>
                          <span className="text-text-secondary ml-2">
                            {u.nationality_name}
                          </span>
                        </div>
                        <div className="text-text-secondary">
                          {u.role === "REFEREE" ? "Referee" : "Umpire"}
                          {u.city ? ` · ${u.city}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pack.venues?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Venues ({pack.venues.length})
                  </h3>
                  <div className="bg-bg-primary border border-border-default rounded-lg max-h-64 overflow-y-auto divide-y divide-border-default">
                    {pack.venues.map((v) => (
                      <div key={v.id} className="px-3 py-2 flex items-center justify-between text-xs">
                        <div>
                          <span className="text-text-primary">{v.name}</span>
                          <span className="text-text-secondary ml-2">
                            {[v.city, v.nationality_name].filter(Boolean).join(" · ")}
                          </span>
                        </div>
                        <div className="text-text-secondary">
                          {v.floodlights ? `${v.floodlights} floodlights` : ""}
                          {v.established_year ? ` · est. ${v.established_year}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pack.players?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-text-primary text-sm font-semibold mb-2">
                    Players ({pack.players.length})
                  </h3>
                  <div className="bg-bg-primary border border-border-default rounded-lg max-h-64 overflow-y-auto divide-y divide-border-default">
                    {pack.players.map((p) => (
                      <div
                        key={p.id}
                        className="px-3 py-2 flex items-center justify-between text-xs"
                      >
                        <div>
                          <span className="text-text-primary">
                            {p.nick_name ||
                              `${p.first_name}${
                                p.last_name ? " " + p.last_name : ""
                              }`}
                          </span>
                          <span className="text-text-secondary ml-2">
                            {p.nationality_name}
                          </span>
                        </div>
                        <div className="text-text-secondary">
                          {p.role || ""}{" "}
                          {p.batting_hand ? `· ${p.batting_hand[0]}HB` : ""}
                          {p.bowling_hand ? ` / ${p.bowling_hand[0]}HM` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {err && (
                <div className="mb-3 bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded-lg p-3 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              {success && (
                <div className="mb-3 bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 rounded-lg p-3 text-sm flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{success}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {pack && (
          <div className="px-5 py-4 border-t border-border-default flex items-center justify-between gap-3 sticky bottom-0 bg-bg-main">
            <div>
              <p className="text-text-secondary text-xs">Price</p>
              <p className="text-text-primary text-xl font-semibold">
                {pack.price_credits} ZC
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  (₹{pack.price_credits.toLocaleString()})
                </span>
              </p>
              {pack.player_count > 0 && (
                <p className="text-[10px] text-text-secondary">
                  ≈ ₹{Math.round(pack.price_credits / pack.player_count)}/player
                </p>
              )}
            </div>
            {pack.already_owned ? (
              <Link
                to="/cricket/players"
                className="px-4 py-2 rounded-lg border border-border-default text-text-primary hover:bg-bg-primary text-sm inline-flex items-center gap-2"
              >
                <Check className="w-4 h-4" /> You own this — open library
              </Link>
            ) : (
              <button
                onClick={handleBuy}
                disabled={anyBuying || !!success}
                className="px-5 py-2 rounded-lg bg-button-primary hover:bg-button-primary/85 disabled:opacity-60 text-bg-main font-semibold text-sm inline-flex items-center gap-2 transition"
              >
                {buying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Purchasing…
                  </>
                ) : success ? (
                  "Done"
                ) : pack.pack_type === "TOURNAMENT_BUNDLE" ? (
                  `Buy full bundle for ${pack.price_credits} ZC`
                ) : (
                  `Buy for ${pack.price_credits} ZC`
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
